import { useContext } from "react";
import { useSelector } from "react-redux";

import Loaders, { BoxLoader } from "@stadium/ui/src/components/loaders/loaders";

import { ExternalButton } from "@components/shared/buttons";
import CartCheckout from "@components/store/cart/cart-checkout";
import SendToMyselfCart from "@components/store/cart/send-to-many/send-to-myself";
import SendToOthersCart from "@components/store/cart/send-to-many/send-to-others";
import CartFooter from "@sharedComponents/cart-footer";
import { EmptyBag } from "@sharedComponents/cart/common";
import SendToManyCountrySelectorModal from "@sharedComponents/cart/send-to-many/send-to-many-country-selector-modal";
import SendToManyVariantSelectorModal from "@sharedComponents/cart/send-to-many/send-to-many-variant-selector-modal";
import MinimalFooter from "@stadium/ui/src/components/footer";
import CartCheckoutCta from "@components/store/cart/cart-checkout-cta";
import Portal from "@stadium/ui/src/components/portal";
import { CartContinueShoppingCta } from "@components/cart/wrapper";

import { getMenuRoute } from "@constants/routes";
import { getStoreCurrencyDetails } from "@selectors/store";
import Wallet from "@stadium/checkout/src/components/payment-options/wallet";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { useStoreContext } from "../provider";

import { useCartListingContext } from "@contexts/cart-listing-context";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useSendItemsContext } from "@contexts/send-items-context";

import useFetchVariantList from "@hooks/cart/use-fetch-variant-list";

import {
  getCartOrderFetching,
  getCartOrderId,
  getOrderTeamWalletId,
  getOrderUseGlobalPoint,
  getOrderUseStoreSpecificPoints,
  getOrderUseWalletMoney,
  getCartOrderAllowAnnonymousInvite,
} from "@selectors/cart";

import { getStoreIsOrderFetching } from "@store/selectors/store";

import { useCheckoutContext } from "@contexts/checkout-context";
import { useCheckoutContext as useGlobalCheckoutContext } from "@stadium/checkout/src/contexts/checkout";

import { NEXT, SEND_TO_MYSELF, YOUR_BAG_OTHER, YOUR_BAG_SELF } from "@constants/june-so";
import { CONTINUE_SHOPPING_TARGET } from "@constants/analytics/cart";

import styles from "./main.module.scss";

const CartListingSendToMany = ({ editable = true, cmsSectionIndex = 0 }) => {
  const { storeBasePath } = useStoreContext();
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const useWalletMoney = useSelector(getOrderUseWalletMoney);
  const useGlobalPoint = useSelector(getOrderUseGlobalPoint);
  const defaultUseStoreSpecificPoints = useSelector(getOrderUseStoreSpecificPoints);
  const storeDetails = useSelector(getStoreCurrencyDetails);
  const defaultTeamWalletId = useSelector(getOrderTeamWalletId);
  const orderId = useSelector(getCartOrderId);
  const orderFetching = useSelector(getCartOrderFetching);
  const isStoreOrderFetching = useSelector(getStoreIsOrderFetching);
  const allowAnonymousInvite = useSelector(getCartOrderAllowAnnonymousInvite);

  const { isGuestOrPreviewMode, previewMode } = usePreviewModeContext();

  const { isSendToMyself, isCountrySelectorModalOpen } = useSendItemsContext();

  const { isSwagVariantSelectorModalOpen } = useCartListingContext();
  const {
    loading: isCardsAndWalletLoading,
    cartTotal,
    cartItemsQuantity,
    teamWallets,
    getCartTotalInSelectedCurrency,
  } = useCheckoutContext();

  const { currency } = useGlobalCheckoutContext();

  const cartProductsData = useFetchVariantList();

  const cartTotalInSelectedCurrency = getCartTotalInSelectedCurrency({
    totalToConvert: cartTotal,
  });

  const renderContinueShoppingCta = () => (
    <div className={styles.backCtaBlock}>
      <ExternalButton
        href={getMenuRoute({ storeBasePath })}
        styleType="link"
        className={styles.continueLink}
        eventLocationAnalytics={{ track: true, target: CONTINUE_SHOPPING_TARGET }}
      >
        <i className={styles.iconArrowLeft} />{" "}
        {isMobileDevice ? "BACK" : <span>{!isSendToMyself ? "Back" : "Continue SHOPPING"}</span>}
      </ExternalButton>
    </div>
  );

  const renderCartCheckoutCta = () => {
    const checkoutButtonJuneSoProps = {
      componentName: YOUR_BAG_SELF,
      target: SEND_TO_MYSELF,
    };

    const nextButtonJuneSoProps = {
      componentName: YOUR_BAG_OTHER,
      target: NEXT,
    };

    return (
      <CartCheckoutCta
        ctaJuneSoProps={isSendToMyself ? checkoutButtonJuneSoProps : nextButtonJuneSoProps}
      />
    );
  };

  const renderFooter = () => {
    if (!isMobileDevice && isSendToMyself) {
      return <MinimalFooter />;
    }
    return (
      <CartFooter
        backCta={renderContinueShoppingCta}
        nextCta={renderCartCheckoutCta}
        className={previewMode ? styles.previewModeFooter : ""}
      />
    );
  };

  const renderCartListing = () =>
    isSendToMyself ? (
      <SendToMyselfCart editable={editable} cartProductsData={cartProductsData} />
    ) : (
      <SendToOthersCart cartProductsData={cartProductsData} />
    );

  const isPageLoading = (isStoreOrderFetching && !isGuestOrPreviewMode) || orderFetching;

  if (isPageLoading)
    return (
      <div>
        <Loaders.PageLoader wrapperClass="pageLoaderwrap min-h-100" />
      </div>
    );

  if (cartItemsQuantity === 0)
    return (
      <EmptyBag menuPath={getMenuRoute({ storeBasePath })} className={styles.onlyEmptySection} />
    );

  const renderWalletDetails = () => {
    if (isSendToMyself && cartItemsQuantity)
      return isCardsAndWalletLoading ? (
        <BoxLoader className={styles.walletLoader} />
      ) : (
        <Wallet
          teamWallets={teamWallets}
          orderTotal={cartTotalInSelectedCurrency}
          billingCurrencyCode={currency?.code}
          defaultValues={{
            defaultUseWallet: useWalletMoney,
            defaultUseStadiumPoints: useGlobalPoint,
            defaultUseStoreSpecificPoints,
            defaultTeamWalletId,
          }}
          storeDetails={storeDetails}
          isOpenOrder={allowAnonymousInvite}
          showStadiumPoints={!isGuestOrPreviewMode}
          showWalletGroup={!isGuestOrPreviewMode}
          eventLocationAnalytics={{
            track: true,
          }}
        />
      );
  };

  const renderSendToManyVariantSelectorModal = () => {
    if (isSwagVariantSelectorModalOpen) return <SendToManyVariantSelectorModal />;
  };

  return (
    <>
      <Portal
        id="cart-back-cta-container"
        dataContainerId="cart-back-cta-portal"
        className={styles.backCtaContainer}
      >
        {!isMobileDevice && isSendToMyself && <CartContinueShoppingCta />}
      </Portal>
      <section className={`${styles.wrapper} wrapper`}>
        <div className={`${styles.myBagContainer} ${previewMode ? styles.extraMargin : ""}`}>
          <div
            className={`${styles.rowBlockgroup} ${
              cmsSectionIndex === 0 ? "" : styles.midCmsSection
            }`}
          >
            <div
              className={`${styles.leftBlock} ${!isSendToMyself ? styles.fullColWidth : ""} ${
                isMobileDevice ? styles.youPayFixPositionInMobile : ""
              }
              `}
            >
              <div className={`${styles.bagHeading} ${isSendToMyself ? styles.myselfHeading : ""}`}>
                My Bag {isSendToMyself && cartItemsQuantity ? `(${cartItemsQuantity})` : null}
              </div>
              {isSendToMyself && isMobileDevice && <CartCheckout editable={editable} />}
              {renderWalletDetails()}
              {renderCartListing()}
            </div>

            {isSendToMyself && !isMobileDevice && (
              <div className={styles.rightBlock}>
                <CartCheckout editable={editable} />
              </div>
            )}
          </div>
        </div>
        {renderSendToManyVariantSelectorModal()}
        {orderId && isCountrySelectorModalOpen && <SendToManyCountrySelectorModal />}
      </section>
      <Portal id="cart-footer-container" dataContainerId="cart-footer-portal">
        {renderFooter()}
      </Portal>
    </>
  );
};

export default CartListingSendToMany;
